import React, { useContext, useEffect, useState, useCallback } from 'react';
import AdminNav from "../Navbars/AdminNav";
import StudentNav from '../Navbars/StudentNav';
import GuestNav from "../Navbars/GuestNav";
import Footer from '../Footer';
import { UserContext } from '../UserContext';
import TeacherNav from "../Navbars/TeacherNav";
import './AttendClass.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import Header from '../Header';

const AttendClass = () => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [currentClassLink, setCurrentClassLink] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentTeacherIds, setCurrentTeacherIds] = useState('');
  const [error, setError] = useState(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  let classLink = queryParams.get('classLink') || '';
  if (classLink && classLink.startsWith("https://")) {
    classLink = classLink.slice(8);
  }
  const course_id = queryParams.get('Course_id') || '';
  const teacherIds = queryParams.get('teacherIds') || '';
  console.log("Query params:", { classLink, course_id, teacherIds });

  const { isAdmin, isTeacher, isStudent, isGuest, setUserRole, setUserInfo, userInfo } = useContext(UserContext);

  const handleClick = (value) => {
    console.log("Rating selected:", value);
    setRating(value);
  };

  const handleMouseOver = (value) => {
    setHoverRating(value);
  };

  const handleMouseLeave = () => {
    setHoverRating(0);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}`, {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }
        const userInfo = await response.json();
        console.log("User info:", userInfo);
        setUserInfo(userInfo);
        if (userInfo?.usertype) {
          const uType = userInfo.usertype;
          setUserRole(uType);
        }
      } catch (err) {
        console.error("Error in useEffect:", err);
        setError('Failed to load user information. Please try again.');
      }
    };
    fetchData();
  }, [isAdmin, isTeacher, isStudent, isGuest, setUserInfo, setUserRole]);

  const determineUserRole = (usertype) => {
    console.log("In home.js:", usertype);
    if (usertype === 'Student') return "Student";
    else if (usertype === 'Teacher') return "Teacher";
    else if (usertype === 'Admin') return "Admin";
    else return "guest";
  };

  const navigate = useNavigate();
  const onLogin = (usertype) => {
    const userRole = determineUserRole(usertype);
    setUserRole(userRole);
  };

  async function onLogout() {
    try {
      await fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/logout`, {
        credentials: 'include',
        method: 'POST',
      });
      setUserInfo(null);
      navigate('/');
      setUserRole('guest');
    } catch (err) {
      console.error("Error logging out:", err);
      setError('Failed to log out. Please try again.');
    }
  }

  const isClassLinkEqual = (oldLink, newLink) => {
    return oldLink === newLink;
  };

  const fetchAndUpdateClassLink = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/fetchStudentRegisteredCourses`, {
        withCredentials: true,
      });
      console.log("API response:", response.data);
      const matchingCourse = response.data.courseNames.find(
        course => course.Course_id === parseInt(course_id)
      );
      if (matchingCourse) {
        if (!isClassLinkEqual(currentClassLink, matchingCourse.classlink)) {
          let newClassLink = matchingCourse.classlink || '';
          if (newClassLink.startsWith("https://")) {
            newClassLink = newClassLink.slice(8);
          }
          setCurrentClassLink(newClassLink);
        }
      } else {
        console.warn(`No matching course found for Course_id: ${course_id}`);
        setError(`Course with ID "${course_id}" not found.`);
      }
    } catch (error) {
      console.error("Error fetching class link:", error);
      toast.error('Failed to update class link. Please try again.', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [course_id, currentClassLink]);

  useEffect(() => {
    if (!course_id) {
      setError('Invalid course data. Please select a course again.');
      return;
    }
    setCurrentClassLink(classLink);
    setCurrentTeacherIds(teacherIds);
    fetchAndUpdateClassLink();
  }, [classLink, teacherIds, course_id, fetchAndUpdateClassLink]);

  useEffect(() => {
    const pollInterval = setInterval(fetchAndUpdateClassLink, 10000);
    return () => clearInterval(pollInterval);
  }, [fetchAndUpdateClassLink]);

  const handleSubmitRating = async () => {
    if (!rating) {
      toast.error('Please select a rating', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }
    if (!course_id) {
      toast.error('Course ID is missing', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }
    if (!currentTeacherIds) {
      toast.error('Teacher information is unavailable. Feedback cannot be submitted.', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }

    const data = {
      rating,
      course_id,
      teacherIds: currentTeacherIds,
      comment: comment || '',
    };

    try {
      const response = await axios.post(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/saveStudentFeedback`, data, {
        withCredentials: true,
      });
      console.log('Feedback response:', response.data);
      if (response.data.message === 'Teacher ratings stored successfully') {
        toast.success('Feedback submitted successfully', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        setRating(0);
        setComment('');
      } else {
        throw new Error(response.data.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error.response ? error.response.data : error.message);
      const errorMessage = error.response?.data?.error || 'Failed to submit feedback. Please try again.';
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const username = userInfo?.username || 'Guest';
  const avatar = userInfo?.avatar || 'https://via.placeholder.com/40';

  const isFeedbackEnabled = isStudent && course_id && currentTeacherIds;

  if (error) {
    return (
      <div className="course-page">
        {/* <Header /> */}
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // if (!userInfo) {
  //   return (
  //     <div className="course-page">
  //       <Header />
  //       <div className="error-message">Loading user information...</div>
  //     </div>
  //   );
  // }

  // if (!isStudent) {
  //   return (
  //     <div className="course-page">
  //       <Header />
  //       <div className="error-message">Access denied. This page is for students only.</div>
  //     </div>
  //   );
  // }

  return (
    <div className="course-page">
      {username && (
        <>
          {isAdmin && <AdminNav username={username} avatar={avatar} onLogout={onLogout} />}
          {isTeacher && <TeacherNav username={username} avatar={avatar} onLogout={onLogout} />}
          {isStudent && <StudentNav username={username} avatar={avatar} onLogout={onLogout} />}
        </>
      )}
      {!username && (
        <>
          {isGuest && <GuestNav onLogin={onLogin} />}
        </>
      )}
        <Header title= "Attend Class"/>
      {/* <div className="right-header">
        <span className="notification-icon">🔔</span>
        <span className="user-name">{username}</span>
        <img src={avatar} alt="User Image" className="user-image" />
      </div> */}

      <div className="content-wrapper">
        
        <button
          className="join-class-btn"
          onClick={() => currentClassLink && window.open(`https://${currentClassLink}`, "_blank")}
          disabled={!currentClassLink || isRefreshing}
        >
          {isRefreshing ? 'Refreshing...' : 'Join Class'}
        </button>

        <div className="feedback-section">
          <p className="feedback-text">Please share your feedback for this class</p>

          {!isStudent && (
            <p className="feedback-warning">Only students can submit feedback.</p>
          )}
          {!course_id && (
            <p className="feedback-warning">Course ID is missing. Feedback cannot be submitted.</p>
          )}
          {!currentTeacherIds && (
            <p className="feedback-warning">Teacher information is unavailable. Please contact support to submit feedback.</p>
          )}

          <div className="star-rating">
            {[1, 2, 3, 4, 5].map((value) => (
              <FontAwesomeIcon
                key={value}
                style={{ color: '#03b4c6', fontSize: "25px" }}
                icon={value <= (hoverRating || rating) ? solidStar : regularStar}
                className="mx-2"
                onClick={() => handleClick(value)}
                onMouseOver={() => handleMouseOver(value)}
                onMouseLeave={handleMouseLeave}
                disabled={!isFeedbackEnabled}
              />
            ))}
          </div>

          <input
            type="text"
            placeholder="Comments"
            className="comments-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={!isFeedbackEnabled}
          />

          <button
            className="submit-rating-btn"
            onClick={handleSubmitRating}
            disabled={!isFeedbackEnabled}
          >
            Submit Rating
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AttendClass;